import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, Loader2, Check, Timer, Gift, Zap, Crown } from 'lucide-react';
import { MagneticButton, Particles } from './PremiumInteractions';
import SuccessConfetti from './SuccessConfetti';

const API_URL = '/.netlify/functions/subscribe';

const LAUNCH_DATE = new Date(import.meta.env.VITE_LAUNCH_DATE || '2025-03-01T20:00:00Z');

const benefits = [
  {
    icon: Crown,
    title: "Early Access",
    description: "Be among the first to get on board"
  },
  {
    icon: Gift,
    title: "Exclusive Perks",
    description: "Access to a Private Development Group"
  },
  {
    icon: Zap,
    title: "Priority Support",
    description: "VIP premium support team"
  }
];

export default function Newsletter() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = LAUNCH_DATE.getTime() - now;

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch(
        API_URL,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ email }),
        });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setEmail('');
        console.log('Subscription successful');
      } else {
        setStatus('error');
        setErrorMessage(data.error || 'Something went wrong. Please try again.');
        console.error('Server error:', data.error);
      }
    } catch (error) {
      console.error('Network error:', error);
      setStatus('error');
      setErrorMessage(
        'Unable to connect to the server. Please make sure the server is running (npm run server) and try again.'
      );
    }
  };

  return (
    <section id="newsletter" className="py-24 bg-gradient-to-b from-zinc-900 to-black relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23fff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <motion.div 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="max-w-4xl mx-auto px-4 text-center relative z-10"
      >
        <div className="mb-12">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-200 to-amber-400">
              Join the Waitlist
            </span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-gray-400 text-lg"
          >
            Be part of the future of luxury fleet management
          </motion.p>
        </div>

        {/* Countdown Timer */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <div className="text-gray-400 mb-4 flex items-center justify-center gap-2">
            <Timer className="w-5 h-5" />
            <span>Early Access Launch in:</span>
          </div>
          <div className="flex justify-center gap-4">
            {Object.entries(timeLeft).map(([unit, value]) => (
              <div key={unit} className="text-center">
                <div className="text-3xl font-bold text-amber-400">{value}</div>
                <div className="text-sm text-gray-500 capitalize">{unit}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="bg-zinc-800/30 p-6 rounded-xl border border-zinc-700"
              >
                <div className="flex justify-center mb-4">
                  <div className="p-2 rounded-lg bg-amber-400/10">
                    <Icon className="w-6 h-6 text-amber-400" />
                  </div>
                </div>
                <h3 className="text-white font-semibold mb-2">{benefit.title}</h3>
                <p className="text-gray-400 text-sm">{benefit.description}</p>
              </motion.div>
            );
          })}
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4 max-w-2xl mx-auto">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your business email"
            className="flex-1 px-6 py-4 rounded-full bg-zinc-800/50 text-white 
                     border border-zinc-700 focus:border-amber-500/50
                     focus:outline-none focus:ring-2 focus:ring-amber-500/20
                     transition-all duration-300"
            required
            disabled={status === 'loading'}
          />
          <MagneticButton
            onClick={() => {}}
            className="relative px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-600 
                     text-black rounded-full hover:from-amber-500 hover:to-amber-700 
                     transition-all duration-300 flex items-center justify-center gap-2
                     disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <Particles color="#000" />
            {status === 'loading' ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Joining...
              </>
            ) : (
              <>
                Join Waitlist
                <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </MagneticButton>
        </form>

        {/* Status Messages */}
        {status === 'success' && (
          <motion.div 
            className="relative mt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <SuccessConfetti />
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-green-500/10 text-green-400 p-4 rounded-lg inline-flex items-center gap-2"
            >
              <Check className="w-5 h-5" />
              <span className="font-medium">Successfully joined! Check your email for confirmation.</span>
            </motion.div>
          </motion.div>
        )}
        {status === 'error' && (
          <motion.p 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 text-red-400"
          >
            {errorMessage}
          </motion.p>
        )}
      </motion.div>
    </section>
  );
} 